import { MIDDOT, PALETTE, type PersonDto } from '@betreuung/shared';
import { AlertTriangle } from 'lucide-react';
import { Swatch } from '@/components/shared/Swatch';
import { de } from '@/i18n/de';

export interface ColorPickerGridProps {
  value: number;
  onChange: (colorIndex: number) => void;
  /** Every person, so colours already in use can be marked. */
  persons: PersonDto[];
  /** The person being edited must not mark its own colour as taken. */
  excludePersonId: number | null;
}

/**
 * The full twelve-colour palette. Assigning a colour twice is allowed — the grid
 * marks who already holds it and warns about the current choice instead of
 * blocking it.
 */
export function ColorPickerGrid({
  value,
  onChange,
  persons,
  excludePersonId,
}: ColorPickerGridProps) {
  // Built from every person, so an abbreviation does not change while the person
  // holding the shorter one is being edited.
  const abbreviations = abbreviate(persons);
  // Only Betreuungspersonen actually claim a colour: a view-only person keeps a
  // stored one that nothing renders, so warning about a clash with them would be
  // a clash that can never be seen.
  const others = persons.filter(
    (person) => person.id !== excludePersonId && person.isCaregiver,
  );

  const owners = new Map<number, PersonDto[]>();
  for (const person of others) {
    const forColor = owners.get(person.colorIndex);
    if (forColor) forColor.push(person);
    else owners.set(person.colorIndex, [person]);
  }
  const selectedOwners = owners.get(value) ?? [];

  return (
    <>
      <div className="grid grid-cols-6 gap-2">
        {PALETTE.map((color) => (
          <button
            key={color.index}
            type="button"
            aria-label={de.a11y.selectColor(color.name)}
            aria-pressed={color.index === value}
            onClick={() => onChange(color.index)}
            className="flex items-center justify-center rounded-control"
          >
            <Swatch
              colorIndex={color.index}
              size={44}
              radius={10}
              selected={color.index === value}
              overlay={(owners.get(color.index) ?? [])
                .map((owner) => abbreviations.get(owner.id) ?? '')
                .join(MIDDOT)}
            />
          </button>
        ))}
      </div>

      <p className="pt-1.5 text-duration text-fg-hint">{de.personSheet.colorHint}</p>

      {selectedOwners.length > 0 ? (
        <p className="flex items-start gap-1.5 pt-1 text-duration text-fg-secondary">
          <AlertTriangle className="mt-px size-3 shrink-0 text-cal-2" />
          {de.personSheet.colorWarning(selectedOwners.map((owner) => owner.name).join(', '))}
        </p>
      ) : null}
    </>
  );
}

/**
 * Overlay label per person: the first letter, or the first three when an earlier
 * person already claimed that letter — the reference shows "Sven" as `S` and
 * "Schule", competing with it, as `Sch`.
 */
function abbreviate(persons: readonly PersonDto[]): Map<number, string> {
  const claimed = new Set<string>();
  const labels = new Map<number, string>();

  for (const person of persons) {
    const name = person.name.trim();
    const first = name.slice(0, 1);
    labels.set(person.id, claimed.has(first.toLowerCase()) ? name.slice(0, 3) : first);
    claimed.add(first.toLowerCase());
  }
  return labels;
}
